import { MenuItem, type SelectChangeEvent, styled } from '@mui/material';
import type { FieldConfigurationDto } from '@tempo-io/fim3-dto';
import { useTranslation } from 'react-i18next';
import { Select } from '../Select';

export const StyledSelect = styled(Select, {
  shouldForwardProp: (prop) => prop !== 'isEditableText',
})<{ isEditableText?: boolean }>(({ theme, isEditableText }) =>
  isEditableText
    ? {
        display: 'flex',
        alignItems: 'center',
        height: 30,
        '& p': {
          display: 'none',
        },
        '.MuiInputBase-root': {
          marginLeft: theme.spacing(-2),
          paddingLeft: theme.spacing(0.5),
          width: '100%',
          height: 30,
        },
        '.MuiInputBase-input': {
          paddingLeft: theme.spacing(0.5),
        },
        '& fieldset': {
          opacity: 0,
        },
        '&:hover fieldset': {
          opacity: 1,
        },
        '& .MuiSvgIcon-root': {
          opacity: 0,
        },
        '&:hover .MuiSvgIcon-root': {
          opacity: 1,
        },
        '.Mui-focused ': {
          ' & .MuiOutlinedInput-notchedOutline': {
            opacity: 1,
          },
        },
      }
    : {},
);

export const SelectFieldInput = ({
  fieldConfiguration,
  values,
  setValues,
  isEditableText,
  includeClearOption,
  ...rest
}: {
  fieldConfiguration: FieldConfigurationDto;
  values: string[];
  setValues: (values: string[]) => void;
  isEditableText?: boolean;
  includeClearOption?: boolean;
  autoFocus?: boolean;
}) => {
  const { t: a11y } = useTranslation('translation');
  if (fieldConfiguration.fieldType !== 'LIST') return;

  const onSelectChange = (event: SelectChangeEvent<unknown>, _child: React.ReactNode) => {
    setValues(
      Array.isArray(event.target.value) ? event.target.value : [event.target.value as string],
    );
  };

  return (
    <StyledSelect
      slotProps={{
        input: { 'aria-label': a11y('ideaManagement.a11y.selectField') },
      }}
      className="fs-exclude"
      data-dd-action-name="FIM 3 Custom Field - select field redacted"
      data-testid={'fim3-custom-field-input-select-input'}
      isEditableText={isEditableText}
      value={values[0] || ''}
      onChange={onSelectChange}
      type={'number'}
      size="small"
      defaultOpen={rest.autoFocus}
      {...rest}
    >
      {includeClearOption && (
        <MenuItem data-testid={'fim3-custom-field-input-select-input-clear-option'} value="">
          None
        </MenuItem>
      )}
      {fieldConfiguration.options.map((option) => (
        <MenuItem
          data-testid={`fim3-custom-field-input-select-input-${option}`}
          key={option}
          value={option}
        >
          {option}
        </MenuItem>
      ))}
    </StyledSelect>
  );
};
